import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Globe, Linkedin, Instagram, Facebook, Youtube, Heart, Send } from "lucide-react";
import { COMPANY_INFO } from "@/data/site";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", { email });
      toast.success(data.already ? "You're already subscribed!" : "Subscribed! Check your inbox.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Subscription failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-[#0A2540] text-slate-300" data-testid="site-footer">
      <div className="container mx-auto max-w-7xl px-6 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-10 h-10 bg-[#2563EB] hex-clip flex items-center justify-center relative">
                <div className="w-10 h-10 scale-[0.7] bg-[#0A2540] hex-clip absolute inset-0 m-auto" />
                <span className="relative text-white font-extrabold text-lg z-10" style={{ fontFamily: "Outfit" }}>E</span>
              </div>
              <div className="leading-tight">
                <div className="font-extrabold text-white text-[15px]" style={{ fontFamily: "Outfit" }}>EASYWORKS</div>
                <div className="text-[9px] tracking-[0.22em] text-slate-400">INDUSTRIES PVT. LTD.</div>
              </div>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed mb-5">
              Empowering businesses through technology, innovation, automation and expert project management.
            </p>
            <div className="flex items-center gap-2">
              <a href={COMPANY_INFO.socials.linkedin} className="w-8 h-8 rounded-md bg-white/10 hover:bg-[#2563EB] flex items-center justify-center transition-colors"><Linkedin className="w-4 h-4" /></a>
              <a href={COMPANY_INFO.socials.instagram} className="w-8 h-8 rounded-md bg-white/10 hover:bg-[#2563EB] flex items-center justify-center transition-colors"><Instagram className="w-4 h-4" /></a>
              <a href={COMPANY_INFO.socials.facebook} className="w-8 h-8 rounded-md bg-white/10 hover:bg-[#2563EB] flex items-center justify-center transition-colors"><Facebook className="w-4 h-4" /></a>
              <a href={COMPANY_INFO.socials.youtube} className="w-8 h-8 rounded-md bg-white/10 hover:bg-[#2563EB] flex items-center justify-center transition-colors"><Youtube className="w-4 h-4" /></a>
            </div>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4 text-[15px]">Quick Links</h4>
            <ul className="space-y-2.5 text-sm">
              {[
                ["Home", "/"], ["About Us", "/about"], ["Our Companies", "/companies"],
                ["Services", "/services"], ["Industries", "/industries"], ["Portfolio", "/portfolio"],
              ].map(([l, p]) => (
                <li key={p}><Link to={p} className="text-slate-400 hover:text-white transition-colors">{l}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4 text-[15px]">Resources</h4>
            <ul className="space-y-2.5 text-sm">
              {[
                ["Case Studies", "/case-studies"], ["Blog", "/resources"], ["Careers", "/resources"],
                ["FAQs", "/resources"], ["Privacy Policy", "/resources"], ["Terms & Conditions", "/resources"],
              ].map(([l, p], i) => (
                <li key={i}><Link to={p} className="text-slate-400 hover:text-white transition-colors">{l}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4 text-[15px]">Contact Us</h4>
            <ul className="space-y-3 text-sm text-slate-400">
              <li className="flex gap-2.5">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-[#2563EB]" />
                <span>
                  <strong className="text-white">Head Office</strong><br />India
                </span>
              </li>
              <li className="flex gap-2.5">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-[#2563EB]" />
                <span>
                  <strong className="text-white">Regional Office</strong><br />UAE
                </span>
              </li>
              <li className="flex gap-2.5"><Phone className="w-4 h-4 mt-0.5 shrink-0 text-[#2563EB]" /><a href={`tel:${COMPANY_INFO.phoneDial}`} className="hover:text-white">IN · {COMPANY_INFO.phone}</a></li>
              <li className="flex gap-2.5"><Phone className="w-4 h-4 mt-0.5 shrink-0 text-[#2563EB]" /><a href={`tel:${COMPANY_INFO.phoneUAEDial}`} className="hover:text-white">UAE · {COMPANY_INFO.phoneUAE}</a></li>
              <li className="flex gap-2.5"><Mail className="w-4 h-4 mt-0.5 shrink-0 text-[#2563EB]" /><a href={`mailto:${COMPANY_INFO.email}`} className="hover:text-white break-all">{COMPANY_INFO.email}</a></li>
              <li className="flex gap-2.5"><Globe className="w-4 h-4 mt-0.5 shrink-0 text-[#2563EB]" /><a href="https://easyworksindustries.com" className="hover:text-white">{COMPANY_INFO.website}</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4 text-[15px]">Newsletter</h4>
            <p className="text-sm text-slate-400 mb-4">Subscribe to get latest updates and insights.</p>
            <form onSubmit={subscribe} className="space-y-2" data-testid="newsletter-form">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                required
                data-testid="newsletter-email-input"
                className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-[#2563EB]"
              />
              <Button
                type="submit"
                disabled={submitting}
                data-testid="newsletter-submit-button"
                className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white gap-2"
              >
                {submitting ? "Subscribing..." : (<>Subscribe <Send className="w-3.5 h-3.5" /></>)}
              </Button>
            </form>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-6 flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-slate-500">
          <div>© {new Date().getFullYear()} {COMPANY_INFO.name}. All Rights Reserved.</div>
          <div className="flex items-center gap-1.5">
            Made with <Heart className="w-3.5 h-3.5 text-rose-500 fill-rose-500" /> by Easyworks Team
          </div>
        </div>
      </div>
    </footer>
  );
}
